/*
 * video.js — a track as a short film: the whole route, a fly-in to the start,
 * the camera following the rider along the line with the numbers counting up,
 * a mini profile filling underneath, and a fly-out to the finished route.
 * What Relive and Strava's flyover sell as a subscription, drawn frame by frame
 * on our own canvas and encoded by the browser (CanvasVideoExport → MP4).
 *
 *   var film = GpsVideo.create({ sr, speed, title, sub, color, style, tiles, relief:[urls]|null, units:U, t, brand, foot, dur })
 *   film.plan()                    → { tiles: n }  — which map tiles the camera will need
 *   film.load(onProgress)          → Promise       — fetches them (once; the cache is shared)
 *   film.draw(ctx, tSec, W, H)     — one frame at time t, into any canvas size
 *
 * Pure in time: frame i is a function of t alone, so the encoder may ask for a
 * frame twice (its codec cascade replays) and get the same picture.
 *
 * The map: the site's shared basemap (js/map-tiles.js — OpenFreeMap vector,
 * rendered into 256 px tiles by an off-screen MapLibre; OpenStreetMap raster
 * where there is no WebGL), with the attribution drawn on every frame. The
 * camera's tile set is derived from the camera path itself, sampled every
 * 1/10 s, so a 300 km ride does not render the whole bounding box: only the
 * corridor the film actually shows.
 */
(function () {
    'use strict';
    var TILE = 256, RAD = Math.PI / 180, MAXLAT = 85.0511287798;
    function projX(lon) { return (lon + 180) / 360 * TILE; }
    function projY(lat) {
        lat = Math.max(-MAXLAT, Math.min(MAXLAT, lat));
        var s = Math.sin(lat * RAD);
        return (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * TILE;
    }
    function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
    function smooth(u) { u = clamp(u, 0, 1); return u * u * (3 - 2 * u); }
    function ease(u) { u = clamp(u, 0, 1); return u < 0.5 ? 4 * u * u * u : 1 - Math.pow(-2 * u + 2, 3) / 2; }

    var imgCache = {}; // style|z/x/y → Promise<canvas|null>, shared by every film on the page

    function create(o) {
        var sr = o.sr, U = o.units, t = o.t;
        var style = o.style === 'light' ? 'light' : 'dark';
        var retina = true;
        // ── the line, thinned to what a film can show ──────────────────────
        var keep = window.GpsGeo.simplifyTo(sr.lat, sr.lon, 2500).mask;
        var idx = [];
        for (var i = 0; i < sr.n; i++) if (keep[i]) idx.push(i);
        if (idx[idx.length - 1] !== sr.n - 1) idx.push(sr.n - 1);
        var n = idx.length;
        var X = new Float64Array(n), Y = new Float64Array(n), D = new Float64Array(n);
        var prevLon = null, off = 0;
        for (var k = 0; k < n; k++) {
            var j = idx[k], lon = sr.lon[j];
            if (prevLon !== null) { var dl = lon + off - prevLon; if (dl > 180) off -= 360; else if (dl < -180) off += 360; }
            prevLon = lon + off;
            X[k] = projX(lon + off); Y[k] = projY(sr.lat[j]);
            D[k] = sr.dist[j];
        }
        var D0 = D[0], Dtot = Math.max(1, D[n - 1] - D0);
        var minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        for (k = 0; k < n; k++) { if (X[k] < minX) minX = X[k]; if (X[k] > maxX) maxX = X[k]; if (Y[k] < minY) minY = Y[k]; if (Y[k] > maxY) maxY = Y[k]; }
        var eleMin = Infinity, eleMax = -Infinity;
        if (sr.ele) for (k = 0; k < sr.n; k++) { var e = sr.ele[k]; if (e === e) { if (e < eleMin) eleMin = e; if (e > eleMax) eleMax = e; } }
        var hasEle = isFinite(eleMin) && eleMax > eleMin;
        var timed = !!sr.time && !isNaN(sr.time[0]) && !isNaN(sr.time[sr.n - 1]) && sr.time[sr.n - 1] > sr.time[0];

        // ── layout for a W×H frame ─────────────────────────────────────────
        function lay(W, H) {
            var S = Math.min(W, H) / 1080, portrait = H > W * 1.2;
            var top = (portrait ? 230 : 150) * S, bottom = (portrait ? 520 : 290) * S, side = 70 * S;
            return { S: S, portrait: portrait, top: top, bottom: bottom, side: side };
        }
        // the zoom that fits the whole route in the free part of the frame
        function fitZoom(W, H) {
            var L = lay(W, H);
            var bw = Math.max(1e-9, maxX - minX), bh = Math.max(1e-9, maxY - minY);
            var aw = W - 2 * L.side, ah = H - L.top - L.bottom;
            return clamp(Math.log2(Math.min(aw / bw, ah / bh)), 1, 17);
        }
        function fitCenter(W, H) {
            var L = lay(W, H), Z = fitZoom(W, H), sc = Math.pow(2, Z);
            // the free area is off-centre (title above, numbers below): shift the centre so the route sits in it
            var dy = ((L.bottom - L.top) / 2) / sc;
            return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 + dy, Z: Z };
        }

        // ── time → where the rider is, where the camera is ─────────────────
        var DUR = Math.max(6, o.dur || 15);
        var T1 = Math.min(1.6, DUR * 0.1), T2 = T1 + Math.min(1.3, DUR * 0.09);
        var T4 = DUR - Math.min(1.4, DUR * 0.09), T3 = T4 - Math.min(1.3, DUR * 0.09);
        function progress(tt) { if (tt <= T2) return 0; if (tt >= T3) return 1; return ease((tt - T2) / (T3 - T2)); }
        function at(p) { // interpolated state at distance share p
            var target = D0 + p * Dtot, lo = 0, hi = n - 1;
            while (lo < hi) { var m = (lo + hi) >> 1; if (D[m] < target) lo = m + 1; else hi = m; }
            var b = Math.max(1, lo), a = b - 1, f = D[b] > D[a] ? clamp((target - D[a]) / (D[b] - D[a]), 0, 1) : 0;
            return { k: a, f: f, x: X[a] + (X[b] - X[a]) * f, y: Y[a] + (Y[b] - Y[a]) * f, dist: target - D0, j: idx[f < 0.5 ? a : b] };
        }
        // the camera looks slightly ahead and averages a stretch of the route, so hairpins do not shake it
        var WIN = 0.035;
        function lookAt(p) {
            var sx = 0, sy = 0, c = 0;
            for (var q = -2; q <= 4; q++) { var s = at(clamp(p + q * WIN / 4, 0, 1)); sx += s.x; sy += s.y; c++; }
            return { x: sx / c, y: sy / c };
        }
        var followZ = null;
        function shiftY(W, H, Z) { var L = lay(W, H); return ((L.bottom - L.top) / 2) / Math.pow(2, Z); }
        function camera(tt, W, H) {
            var ov = fitCenter(W, H);
            var Zf = followZ != null ? followZ : Math.min(ov.Z + 2.6, 16.2);
            if (Zf < ov.Z + 0.6) Zf = ov.Z; // a route that already fills the frame is not zoomed into
            if (tt <= T1 || tt >= T4) return ov;
            var c;
            if (tt < T2) { c = lookAt(0); c.y += shiftY(W, H, Zf); var u = smooth((tt - T1) / (T2 - T1)); return { x: ov.x + (c.x - ov.x) * u, y: ov.y + (c.y - ov.y) * u, Z: ov.Z + (Zf - ov.Z) * u }; }
            if (tt > T3) { c = lookAt(1); c.y += shiftY(W, H, Zf); var v = smooth((tt - T3) / (T4 - T3)); return { x: c.x + (ov.x - c.x) * v, y: c.y + (ov.y - c.y) * v, Z: Zf + (ov.Z - Zf) * v }; }
            c = lookAt(progress(tt));
            return { x: c.x, y: c.y + shiftY(W, H, Zf), Z: Zf };
        }

        // ── tiles ──────────────────────────────────────────────────────────
        // the light film sits on the colourful street map, the dark one on the dark basemap
        var mapStyle = style === 'light' ? 'street' : 'dark';
        var mapSource = 'vector';
        var tiles = {}; // 'z/x/y' → canvas, for THIS style
        // relief under the camera: the same shaded Terrarium tiles as the page map,
        // stronger on the dark style (its shadows are invisible on black — the lit
        // slopes carry the mountains)
        var RELIEF = o.relief && window.GpsTerrain ? o.relief : null;
        var RPAL = style === 'light' ? { sh: [22, 28, 46, 88], li: [255, 252, 240, 40] } : { sh: [0, 0, 0, 70], li: [196, 208, 236, 34] };
        var shades = {}; // 'z/x/y' (z ≤ 15) → canvas
        function reliefKey(k2) {
            var p2 = k2.split('/'), z = +p2[0], mz = window.GpsTerrain.MAXZ, f = Math.pow(2, Math.max(0, z - mz));
            return Math.min(z, mz) + '/' + Math.floor(+p2[1] / f) + '/' + Math.floor(+p2[2] / f);
        }
        function tkey(z, x, y) { return style + '|' + z + '/' + x + '/' + y; }
        function fetchTile(z, x, y) {
            var key = tkey(z, x, y);
            if (!imgCache[key]) {
                imgCache[key] = window.__mapTiles && window.__mapTiles.tileImage
                    ? window.__mapTiles.tileImage(mapStyle, z, x, y, { retina: retina }).catch(function () { return null; })
                    : Promise.resolve(null);
            }
            return imgCache[key].then(function (im) {
                if (im) { tiles[z + '/' + x + '/' + y] = im; if (im.mapSource) mapSource = im.mapSource; }
                return im;
            });
        }
        function visibleTiles(cam, W, H, out) {
            var zt = clamp(Math.round(cam.Z), 1, 18), sc = Math.pow(2, cam.Z - zt), w2 = Math.pow(2, zt);
            var cx = cam.x * w2, cy = cam.y * w2, hw = W / 2 / sc, hh = H / 2 / sc;
            var x0 = Math.floor((cx - hw) / TILE), x1 = Math.floor((cx + hw) / TILE), y0 = Math.floor((cy - hh) / TILE), y1 = Math.floor((cy + hh) / TILE);
            for (var tx = x0; tx <= x1; tx++) for (var ty = y0; ty <= y1; ty++) {
                if (ty < 0 || ty >= w2) continue;
                out[zt + '/' + (((tx % w2) + w2) % w2) + '/' + ty] = 1;
            }
            return out;
        }
        function planTiles(W, H) {
            var need = {};
            for (var tt = 0; tt <= DUR + 1e-6; tt += 0.1) visibleTiles(camera(tt, W, H), W, H, need);
            return Object.keys(need);
        }
        // choose the follow zoom so the corridor stays under ~260 tiles
        function plan(W, H) {
            followZ = null;
            var ov = fitCenter(W, H), Zf = Math.min(ov.Z + 2.6, 16.2), keys;
            for (var guard = 0; guard < 8; guard++) {
                followZ = Zf;
                keys = planTiles(W, H);
                if (keys.length <= 260 || Zf <= ov.Z + 0.6) break;
                Zf -= 0.5;
            }
            return { keys: keys, tiles: keys.length, zoom: followZ };
        }
        var planned = null, plannedFor = '';
        function ensurePlan(W, H) {
            var sig = W + 'x' + H + ':' + DUR;
            if (plannedFor !== sig) { planned = plan(W, H); plannedFor = sig; }
            return planned;
        }
        function load(W, H, onProgress) {
            var P = ensurePlan(W, H), done = 0;
            var queue = P.keys.map(function (k2) { return 't:' + k2; });
            if (RELIEF) {
                var rk = {};
                P.keys.forEach(function (k2) { if (+k2.split('/')[0] <= window.GpsTerrain.MAXZ) rk[reliefKey(k2)] = 1; });
                Object.keys(rk).forEach(function (k2) { if (!shades[k2]) queue.push('r:' + k2); });
            }
            var total = queue.length, active = 0;
            return new Promise(function (resolve) {
                if (!total) { resolve(0); return; }
                var failed = 0;
                function pump() {
                    while (active < 8 && queue.length) {
                        var job = queue.shift(), kind = job.charAt(0), kk = job.slice(2).split('/');
                        active++;
                        (kind === 't' ? fetchTile(+kk[0], +kk[1], +kk[2]) : window.GpsTerrain.shade(RELIEF, +kk[0], +kk[1], +kk[2], RPAL).then((function (key) {
                            return function (cv) { if (cv) shades[key] = cv; return cv || 'none'; };
                        })(job.slice(2)))).then(function (im) {
                            if (!im) failed++;
                            active--; done++;
                            if (onProgress) onProgress(done / total);
                            if (done >= total) resolve(failed); else pump();
                        });
                    }
                }
                pump();
            });
        }

        // ── drawing ───────────────────────────────────────────────────────
        var COL = style === 'light'
            ? { bg: '#eef0f3', faint: 'rgba(40,46,60,.30)', casing: 'rgba(255,255,255,.95)', text: '#101418', sub: 'rgba(16,20,24,.72)', card: 'rgba(255,255,255,.86)', cardLine: 'rgba(0,0,0,.08)', shade: ['rgba(255,255,255,.92)', 'rgba(255,255,255,0)'], prof: 'rgba(20,24,32,.16)' }
            : { bg: '#0d0f14', faint: 'rgba(255,255,255,.30)', casing: 'rgba(0,0,0,.55)', text: '#ffffff', sub: 'rgba(255,255,255,.74)', card: 'rgba(12,14,20,.72)', cardLine: 'rgba(255,255,255,.10)', shade: ['rgba(8,10,14,.86)', 'rgba(8,10,14,0)'], prof: 'rgba(255,255,255,.16)' };
        var ACC = o.color || '#ff5a1f';
        var FONT = 'system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif';

        function drawTiles(ctx, cam, W, H) {
            var zt = clamp(Math.round(cam.Z), 1, 18), sc = Math.pow(2, cam.Z - zt), w2 = Math.pow(2, zt);
            var cx = cam.x * w2, cy = cam.y * w2, hw = W / 2 / sc, hh = H / 2 / sc;
            var x0 = Math.floor((cx - hw) / TILE), x1 = Math.floor((cx + hw) / TILE), y0 = Math.floor((cy - hh) / TILE), y1 = Math.floor((cy + hh) / TILE);
            var size = TILE * sc;
            for (var tx = x0; tx <= x1; tx++) for (var ty = y0; ty <= y1; ty++) {
                if (ty < 0 || ty >= w2) continue;
                var wx = ((tx % w2) + w2) % w2;
                var dx = (tx * TILE - cx) * sc + W / 2, dy = (ty * TILE - cy) * sc + H / 2;
                var im = tiles[zt + '/' + wx + '/' + ty];
                if (im) { ctx.drawImage(im, dx, dy, size + 0.6, size + 0.6); drawRelief(ctx, zt, wx, ty, dx, dy, size); continue; }
                // a missing tile: its parent, enlarged, beats a hole
                for (var up = 1; up <= 3; up++) {
                    var pz = zt - up, f = Math.pow(2, up), pim = tiles[pz + '/' + Math.floor(wx / f) + '/' + Math.floor(ty / f)];
                    if (!pim) continue;
                    var s2 = (pim.naturalWidth || pim.width || 256) / f;
                    ctx.drawImage(pim, (wx % f) * s2, (ty % f) * s2, s2, s2, dx, dy, size + 0.6, size + 0.6);
                    break;
                }
                drawRelief(ctx, zt, wx, ty, dx, dy, size);
            }
        }
        function drawRelief(ctx, zt, wx, ty, dx, dy, size) {
            // past z15 the terrain would be enlarged into soft blobs: it adds mud, not relief
            if (!RELIEF || zt > window.GpsTerrain.MAXZ) return;
            var mz = window.GpsTerrain.MAXZ, f = Math.pow(2, Math.max(0, zt - mz));
            var cv = shades[Math.min(zt, mz) + '/' + Math.floor(wx / f) + '/' + Math.floor(ty / f)];
            if (!cv) return;
            if (f === 1) ctx.drawImage(cv, dx, dy, size + 0.6, size + 0.6);
            else { var s3 = 256 / f; ctx.drawImage(cv, (wx % f) * s3, (ty % f) * s3, s3, s3, dx, dy, size + 0.6, size + 0.6); }
        }
        function toScreen(cam, W, H) {
            var sc = Math.pow(2, cam.Z), cx = cam.x * sc - W / 2, cy = cam.y * sc - H / 2;
            return function (x, y) { return [x * sc - cx, y * sc - cy]; };
        }
        function strokePath(ctx, P, a, b, extra) {
            ctx.beginPath();
            var p0 = P(X[a], Y[a]); ctx.moveTo(p0[0], p0[1]);
            var lx = p0[0], ly = p0[1];
            for (var q = a + 1; q <= b; q++) {
                var pt = P(X[q], Y[q]);
                if ((pt[0] - lx) * (pt[0] - lx) + (pt[1] - ly) * (pt[1] - ly) < 0.6 && q < b) continue;
                ctx.lineTo(pt[0], pt[1]); lx = pt[0]; ly = pt[1];
            }
            if (extra) ctx.lineTo(extra[0], extra[1]);
            ctx.stroke();
        }
        function rr(ctx, x, y, w, h, r) {
            ctx.beginPath(); ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
            ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
        }
        function fit(ctx, text, max, size, weight) {
            var s = size;
            ctx.font = weight + ' ' + s + 'px ' + FONT;
            while (s > size * 0.5 && ctx.measureText(text).width > max) { s -= 1; ctx.font = weight + ' ' + s + 'px ' + FONT; }
            if (ctx.measureText(text).width > max) { while (text.length > 3 && ctx.measureText(text + '…').width > max) text = text.slice(0, -1); text += '…'; }
            return text;
        }
        function valuesAt(p) {
            var s = at(p), j = s.j, v = {};
            v.dist = U.dist(s.dist);
            if (hasEle && sr.ele[j] === sr.ele[j]) v.ele = U.ele(sr.ele[j]);
            if (timed && sr.time[j] === sr.time[j]) v.time = U.dur((sr.time[j] - sr.time[0]) / 1000);
            if (timed && o.speed && o.speed[j] === o.speed[j]) v.speed = o.foot ? U.pace(o.speed[j]) : U.speed(o.speed[j]);
            return v;
        }

        function draw(ctx, tt, W, H) {
            var L = lay(W, H), S = L.S;
            tt = clamp(tt, 0, DUR);
            var cam = camera(tt, W, H), p = progress(tt);
            ctx.save();
            ctx.fillStyle = COL.bg; ctx.fillRect(0, 0, W, H);
            drawTiles(ctx, cam, W, H);
            // legibility: the title and the numbers sit on a soft fade, not a box
            var g1 = ctx.createLinearGradient(0, 0, 0, L.top * 1.25);
            g1.addColorStop(0, COL.shade[0]); g1.addColorStop(1, COL.shade[1]);
            ctx.fillStyle = g1; ctx.fillRect(0, 0, W, L.top * 1.25);
            var g2 = ctx.createLinearGradient(0, H - L.bottom * 1.1, 0, H);
            g2.addColorStop(0, COL.shade[1]); g2.addColorStop(1, COL.shade[0]);
            ctx.fillStyle = g2; ctx.fillRect(0, H - L.bottom * 1.1, W, L.bottom * 1.1);
            // the route
            var P = toScreen(cam, W, H);
            ctx.lineJoin = 'round'; ctx.lineCap = 'round';
            ctx.strokeStyle = COL.faint; ctx.lineWidth = 4.5 * S;
            strokePath(ctx, P, 0, n - 1);
            var s = at(p), head = P(s.x, s.y);
            if (p > 0) {
                ctx.strokeStyle = COL.casing; ctx.lineWidth = 11 * S;
                strokePath(ctx, P, 0, s.k, head);
                ctx.strokeStyle = ACC; ctx.lineWidth = 7 * S;
                strokePath(ctx, P, 0, s.k, head);
            }
            // start, finish, rider
            var st = P(X[0], Y[0]);
            ctx.fillStyle = '#1fb35a'; ctx.strokeStyle = '#fff'; ctx.lineWidth = 3 * S;
            ctx.beginPath(); ctx.arc(st[0], st[1], 9 * S, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
            if (p >= 1) {
                var fn = P(X[n - 1], Y[n - 1]);
                ctx.fillStyle = '#e5484d';
                ctx.beginPath(); ctx.arc(fn[0], fn[1], 9 * S, 0, Math.PI * 2); ctx.fill(); ctx.stroke();
            }
            if (tt > T1 && p < 1) {
                var pulse = 1 + 0.5 * ((tt * 1.6) % 1);
                ctx.fillStyle = hexA(ACC, 0.25 * (1.5 - pulse));
                ctx.beginPath(); ctx.arc(head[0], head[1], 16 * S * pulse, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = '#fff';
                ctx.beginPath(); ctx.arc(head[0], head[1], 10 * S, 0, Math.PI * 2); ctx.fill();
                ctx.fillStyle = ACC;
                ctx.beginPath(); ctx.arc(head[0], head[1], 6.5 * S, 0, Math.PI * 2); ctx.fill();
            }
            // title
            ctx.textBaseline = 'alphabetic'; ctx.textAlign = 'left';
            ctx.fillStyle = COL.text;
            var tx = L.side * 0.8, ty = (L.portrait ? 120 : 84) * S;
            ctx.fillText(fit(ctx, o.title || '', W - 2 * tx, (L.portrait ? 58 : 50) * S, '800'), tx, ty);
            if (o.sub) { ctx.fillStyle = COL.sub; ctx.fillText(fit(ctx, o.sub, W - 2 * tx, (L.portrait ? 32 : 28) * S, '600'), tx, ty + (L.portrait ? 50 : 42) * S); }
            // the numbers
            var v = valuesAt(p);
            var cells = [[t('s_dist'), v.dist]];
            if (v.time) cells.push([t('s_time_video'), v.time]);
            if (v.ele) cells.push([t('p_ele'), v.ele]);
            if (v.speed) cells.push([o.foot ? t('p_pace') : t('p_speed'), v.speed]);
            var cols = L.portrait ? 2 : cells.length;
            var cw = (W - 2 * tx - (cols - 1) * 14 * S) / cols, ch = 118 * S;
            var rows = Math.ceil(cells.length / cols);
            var profH = hasEle ? (L.portrait ? 150 : 96) * S : 0;
            var baseY = H - 46 * S - profH - (profH ? 22 * S : 0) - rows * ch - (rows - 1) * 14 * S;
            cells.forEach(function (c, i2) {
                var cx2 = tx + (i2 % cols) * (cw + 14 * S), cy2 = baseY + Math.floor(i2 / cols) * (ch + 14 * S);
                ctx.fillStyle = COL.card; rr(ctx, cx2, cy2, cw, ch, 18 * S); ctx.fill();
                ctx.strokeStyle = COL.cardLine; ctx.lineWidth = 1.5 * S; ctx.stroke();
                ctx.fillStyle = COL.sub;
                ctx.fillText(fit(ctx, c[0], cw - 36 * S, 24 * S, '600'), cx2 + 18 * S, cy2 + 40 * S);
                ctx.fillStyle = COL.text;
                ctx.fillText(fit(ctx, c[1], cw - 36 * S, 46 * S, '800'), cx2 + 18 * S, cy2 + 94 * S);
            });
            // mini profile with the rider on it
            if (profH) {
                var px0 = tx, pw = W - 2 * tx, py0 = H - 40 * S - profH;
                var span = eleMax - eleMin, base = py0 + profH;
                var prof = new Path2D();
                prof.moveTo(px0, base);
                var step = Math.max(1, Math.floor(sr.n / 600));
                for (var q = 0; q < sr.n; q += step) { var e2 = sr.ele[q]; if (e2 !== e2) continue; prof.lineTo(px0 + (sr.dist[q] - D0) / Dtot * pw, base - (e2 - eleMin) / span * profH * 0.92); }
                prof.lineTo(px0 + pw, base); prof.closePath();
                ctx.fillStyle = COL.prof; ctx.fill(prof);
                // the part already ridden, in the route's colour
                ctx.save();
                ctx.beginPath(); ctx.rect(px0, py0 - 2, pw * p, profH + 4); ctx.clip();
                ctx.fillStyle = hexA(ACC, 0.85); ctx.fill(prof);
                ctx.restore();
                if (p > 0 && p < 1) {
                    var mx = px0 + pw * p;
                    ctx.strokeStyle = COL.text; ctx.lineWidth = 2 * S;
                    ctx.beginPath(); ctx.moveTo(mx, py0 - 6 * S); ctx.lineTo(mx, base); ctx.stroke();
                }
            }
            // brand and the map's attribution — the licence asks for it on every frame
            ctx.font = '600 ' + Math.round(19 * S) + 'px ' + FONT;
            ctx.fillStyle = COL.sub; ctx.textAlign = 'right';
            ctx.fillText(window.__mapTiles && window.__mapTiles.creditText ? window.__mapTiles.creditText(mapSource) : '© OpenStreetMap contributors', W - tx, H - 12 * S);
            ctx.textAlign = 'left';
            ctx.fillText(o.brand || '', tx, H - 12 * S);
            ctx.restore();
        }
        function hexA(hex, a) {
            var h = String(hex).replace('#', '');
            if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
            return 'rgba(' + parseInt(h.slice(0, 2), 16) + ',' + parseInt(h.slice(2, 4), 16) + ',' + parseInt(h.slice(4, 6), 16) + ',' + a + ')';
        }

        return {
            duration: DUR,
            plan: function (W, H) { var P = ensurePlan(W, H); return { tiles: P.tiles, zoom: P.zoom }; },
            load: load,
            draw: draw
        };
    }

    window.GpsVideo = { create: create };
})();
